import { useState, useCallback } from 'react';
import type { PasskeyAuthenticationOptions, PasskeyAuthenticationResponse } from '../types';
import { generateChallenge, bufferToBase64, base64ToBuffer } from '../utils/passkey-utils';

export const usePasskeyAuthentication = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const authenticate = useCallback(async (options?: PasskeyAuthenticationOptions): Promise<PasskeyAuthenticationResponse> => {
    console.log('🔐 Starting authentication with options:', options);
    setIsAuthenticating(true);
    setError(null);

    try {
      const challenge = options?.challenge || generateChallenge();
      
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        timeout: options?.timeout || 60000,
        userVerification: options?.userVerification || 'preferred',
        rpId: window.location.hostname,
      };

      // Only add allowCredentials if we have specific credentials to allow
      if (options?.allowCredentials && options.allowCredentials.length > 0) {
        publicKeyCredentialRequestOptions.allowCredentials = options.allowCredentials.map((id) => ({
          id: base64ToBuffer(id),
          type: 'public-key',
          transports: ['internal', 'hybrid', 'usb', 'ble', 'nfc'] as AuthenticatorTransport[],
        }));
      }

      console.log('📡 Calling navigator.credentials.get with:', publicKeyCredentialRequestOptions);

      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
        mediation: 'optional', // Always use 'optional' for explicit authentication
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Authentication cancelled or failed');
      }

      console.log('✅ Authentication successful, extracting full response data...');

      const response = credential.response as AuthenticatorAssertionResponse;
      const credentialId = bufferToBase64(credential.rawId);
      
      // Build complete authentication response for backend verification
      const authResponse: PasskeyAuthenticationResponse = {
        credentialId,
        challenge: bufferToBase64(challenge as ArrayBuffer),
        signature: bufferToBase64(response.signature),
        clientDataJSON: bufferToBase64(response.clientDataJSON),
        authenticatorData: bufferToBase64(response.authenticatorData),
        userHandle: response.userHandle ? bufferToBase64(response.userHandle) : undefined,
      };
      
      console.log('🎉 Complete authentication response ready:', {
        credentialId: authResponse.credentialId.substring(0, 20) + '...',
        hasSignature: !!authResponse.signature,
        hasClientData: !!authResponse.clientDataJSON,
        hasAuthData: !!authResponse.authenticatorData,
        hasUserHandle: !!authResponse.userHandle,
      });
      
      options?.onSuccess?.(authResponse);
      return authResponse;
    } catch (err) {
      console.error('❌ Authentication error:', err);
      const error = err instanceof Error ? err : new Error('Authentication failed');
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  return {
    authenticate,
    isAuthenticating,
    error,
  };
};